import { TableModel } from 'components/table/IdmTable';
import { DepotSummaryView } from 'core/graphql/CapacityPlanning/models';
import {
  perDriverOf,
  recipientsPerDriverText,
  recipientsText,
} from './perDriver';

/**
 * One row per Planning Depot, plus the totals row the table closes with.
 *
 * A Planning Depot is a logistic location, not a customer address: its
 * coordinates and counts carry no personal data.
 */
export type DepotSummaryRow = {
  /** 0 on the totals row, which carries no depot of its own. */
  depotIndex: number;
  label: string;
  deliveries: number;
  /** Counted per Stop: the same phone at two Stops counts twice. */
  recipients: number;
  stops: number;
  drivers: number;
  deliveriesPerDriver: number;
  routedRecipients: number;
  recipientsPerDriver: number;
  unassignedStops: number;
  unassignedDeliveries: number;
  failedSolves: number;
  isTotal: boolean;
};

export const depotLabel = (depot: DepotSummaryView) =>
  depot.agencyName ?? `Depot ${depot.depotIndex}`;

const routedDeliveriesOf = (depot: DepotSummaryView) =>
  depot.deliveries - depot.failedDeliveries - depot.unassignedDeliveries;

/**
 * Both per-driver ratios are recomputed from the routed counts, also for Runs
 * stored under an older rule.
 */
const depotRow = (depot: DepotSummaryView): DepotSummaryRow => ({
  depotIndex: depot.depotIndex,
  label: depotLabel(depot),
  deliveries: depot.deliveries,
  recipients: depot.recipients,
  stops: depot.stops,
  drivers: depot.drivers,
  deliveriesPerDriver: perDriverOf(routedDeliveriesOf(depot), depot.drivers),
  routedRecipients: depot.routedRecipients,
  recipientsPerDriver: perDriverOf(depot.routedRecipients, depot.drivers),
  unassignedStops: depot.unassignedStops,
  unassignedDeliveries: depot.unassignedDeliveries,
  failedSolves: depot.failedSolves,
  isTotal: false,
});

const sum = (depots: DepotSummaryView[], of: (d: DepotSummaryView) => number) =>
  depots.reduce((total, depot) => total + of(depot), 0);

/**
 * The totals are added up from the rows above rather than read off the Run, so
 * the last row can never disagree with the ones it sums.
 */
export const depotTotalsRow = (depots: DepotSummaryView[]): DepotSummaryRow => {
  const deliveries = sum(depots, (depot) => depot.deliveries);
  const drivers = sum(depots, (depot) => depot.drivers);
  const routedDeliveries = sum(depots, routedDeliveriesOf);
  const routedRecipients = sum(depots, (depot) => depot.routedRecipients);

  return {
    depotIndex: 0,
    label: 'Total',
    deliveries,
    recipients: sum(depots, (depot) => depot.recipients),
    stops: sum(depots, (depot) => depot.stops),
    drivers,
    deliveriesPerDriver: perDriverOf(routedDeliveries, drivers),
    routedRecipients,
    recipientsPerDriver: perDriverOf(routedRecipients, drivers),
    unassignedStops: sum(depots, (depot) => depot.unassignedStops),
    unassignedDeliveries: sum(depots, (depot) => depot.unassignedDeliveries),
    failedSolves: sum(depots, (depot) => depot.failedSolves),
    isTotal: true,
  };
};

export const toDepotSummaryRows = (
  depots: DepotSummaryView[],
): DepotSummaryRow[] =>
  depots.length === 0 ? [] : [...depots.map(depotRow), depotTotalsRow(depots)];

const cell = (value: number | string, row: DepotSummaryRow) => (
  <span className={row.isTotal ? 'font-semibold' : undefined}>{value}</span>
);

const failedSolveClassName = (row: DepotSummaryRow) => {
  if (row.failedSolves > 0) {
    return 'font-semibold text-darker-red';
  }

  return row.isTotal ? 'font-semibold' : undefined;
};

export const depotSummaryTableModel: TableModel<DepotSummaryRow> = {
  columns: [
    {
      title: 'Agency',
      align: 'left',
      width: 3,
      html: (row) => cell(row.label, row),
    },
    {
      title: 'Deliveries',
      align: 'right',
      width: 2,
      tooltip:
        'All deliveries of the agency, including unassigned stops and failed solves',
      html: (row) => cell(row.deliveries.toLocaleString(), row),
    },
    {
      title: 'Recipients',
      align: 'right',
      width: 2,
      tooltip:
        'All recipients of the agency, including unassigned stops and failed solves',
      html: (row) => cell(recipientsText(row.recipients, row.deliveries), row),
    },
    {
      title: 'Stops',
      align: 'right',
      width: 2,
      html: (row) => cell(row.stops.toLocaleString(), row),
    },
    {
      title: 'Drivers',
      align: 'right',
      width: 2,
      html: (row) => cell(row.drivers.toLocaleString(), row),
    },
    {
      title: 'Deliveries per driver',
      align: 'right',
      width: 2,
      tooltip:
        'Only deliveries on routes, divided by drivers: unassigned stops and failed solves are left out',
      html: (row) => cell(row.deliveriesPerDriver.toFixed(1), row),
    },
    {
      title: 'Recipients per driver',
      align: 'right',
      width: 2,
      tooltip:
        'Only recipients on routes, divided by drivers: unassigned stops and failed solves are left out',
      html: (row) => cell(recipientsPerDriverText(row), row),
    },
    {
      title: 'Failed',
      align: 'right',
      width: 2,
      html: (row) => (
        <span className={failedSolveClassName(row)}>{row.failedSolves}</span>
      ),
    },
    {
      title: 'Unassigned',
      align: 'right',
      width: 2,
      html: (row) =>
        cell(
          row.unassignedStops === 0
            ? '0'
            : `${row.unassignedStops} (${row.unassignedDeliveries})`,
          row,
        ),
    },
  ],
};

export default depotSummaryTableModel;
